// Wizard Step 0 — Захиалга picker (screens-buyer-app §3). Path B only.
//
// Props-driven, owns only the local search/filter/page UI state; the order data
// comes from SWR (getDeliveredOrders). Picking an order calls onPick(orderId)
// which advances the wizard to Step 1.
import {
  Box,
  Group,
  Pagination,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { CaretRightIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { HelpIcon, i18n } from "@dehub/theme";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ReturnApi } from "../../../../apis";
import type { IReturnOrderDetail } from "../../../../apis/return";
import { currencyFormat } from "../../../../utils";
import { isOrderReturnable, TNUM } from "../constants";
import { Dot } from "../../../delivery/atoms";
import { ReturnEmptyState } from "../return-empty-state";
import { ReturnNetworkError } from "../return-error-state";

const PAGE_SIZE = 10;
const DAY_MS = 24 * 60 * 60 * 1000;

// §3 date filter — the spec mockup shows "[Сүүлийн 30 хоног ▼]".
const dateOptions = () => [
  { value: "all", label: i18n.t("buyerApp:stepPicker.datePreset.all") },
  { value: "30", label: i18n.t("buyerApp:stepPicker.datePreset.d30") },
  { value: "90", label: i18n.t("buyerApp:stepPicker.datePreset.d90") },
  { value: "365", label: i18n.t("buyerApp:stepPicker.datePreset.y1") },
];

function dateRange(preset: string): { dateFrom?: string; dateTo?: string } {
  if (preset === "all") return {};
  const to = new Date();
  return { dateFrom: new Date(to.getTime() - Number(preset) * DAY_MS).toISOString(), dateTo: to.toISOString() };
}

const orderNumberOf = (o: IReturnOrderDetail): string =>
  o.purchaseCode || o.salesCode || o.orderNumber || o.id;

interface Props {
  onPick: (orderId: string) => void;
  onViewOrders?: () => void;
}

export function StepOrderPicker({ onPick, onViewOrders }: Props) {
  const { t } = useTranslation("buyerApp");
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search, 350);
  const [datePreset, setDatePreset] = useState("all");
  const [page, setPage] = useState(1);

  const { data, error, isLoading } = useSWR(
    ["swr.buyer.returns.delivered-orders", debounced, datePreset, page],
    () =>
      ReturnApi.getDeliveredOrders({
        search: debounced || undefined,
        deliveryNoteStatus: "DELIVERED",
        ...dateRange(datePreset),
        page,
        limit: PAGE_SIZE,
      }),
    { revalidateOnFocus: false, keepPreviousData: true },
  );

  const rows = data?.rows ?? [];
  const total = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Stack gap="md">
      <Box>
        <Text fw={600} size="lg">
          {t("stepPicker.title")}<HelpIcon helpKey="order.buyer-app-my-returns-new.section.order-picker" variant="section" />
        </Text>
      </Box>

      <Group gap="sm" wrap="wrap" align="center">
        <TextInput
          leftSection={<MagnifyingGlassIcon size={18} />}
          placeholder={t("stepPicker.searchPlaceholder")}
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
            setPage(1);
          }}
          style={{ flex: "1 1 280px" }}
        />
        <Select
          data={dateOptions()}
          value={datePreset}
          onChange={(v) => {
            setDatePreset(v ?? "all");
            setPage(1);
          }}
          allowDeselect={false}
          w={{ base: "100%", sm: 240 }}
          aria-label={t("stepPicker.periodAria")}
          leftSection={
            <Text span size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
              {t("stepPicker.periodAria")}
            </Text>
          }
          leftSectionWidth={84}
          leftSectionPointerEvents="none"
          styles={{ input: { fontWeight: 600 } }}
        />
      </Group>

      {error ? (
        <ReturnNetworkError />
      ) : !isLoading && rows.length === 0 ? (
        <ReturnEmptyState
          title={t("stepPicker.emptyTitle")}
          description={t("stepPicker.emptyDescription")}
          actionLabel={onViewOrders ? t("stepPicker.viewOrders") : undefined}
          onAction={onViewOrders}
        />
      ) : (
        <Stack gap="sm" pos="relative">
          {rows.map((order) => {
            const remaining = order.returnWindowDaysRemaining;
            // B64: same eligibility flag as the order page (order-return-entry).
            const eligible = isOrderReturnable(order);
            const itemCount =
              (order as { totalItemCount?: number; itemTypeCount?: number }).itemTypeCount ??
              (order as { totalItemCount?: number }).totalItemCount;
            return (
              <Paper
                key={order.id}
                withBorder
                radius="md"
                p="md"
                onClick={() => onPick(order.id)}
                style={{ cursor: "pointer" }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onPick(order.id);
                }}
              >
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Box style={{ minWidth: 0 }}>
                    <Group gap="xs" wrap="nowrap">
                      <Text fw={700} ff="monospace" fz="var(--nw-fz-lead)">{orderNumberOf(order)}</Text>
                    </Group>
                    <Text size="sm" c="dimmed" mt={4} ff="monospace" style={TNUM}>
                      {order.deliveredAt ? t("stepPicker.deliveredOn", { date: order.deliveredAt.slice(0, 10) }) : t("stepPicker.deliveryDateUnknown")}
                      {order.totalAmount != null ? ` · ${currencyFormat(order.totalAmount)}` : ""}
                    </Text>
                    {itemCount != null ? (
                      <Text size="sm" c="dimmed" mt={2}>
                        {t("stepPicker.itemTypeCount", { count: itemCount })}
                      </Text>
                    ) : null}
                    {!eligible || remaining != null ? (
                      <Group gap={8} mt={6} wrap="nowrap" align="center">
                        <Dot
                          tone={!eligible || (remaining ?? 0) <= 0 ? "grey" : (remaining ?? 0) <= 2 ? "amber" : "green"}
                        />
                        <Text size="sm">
                          {!eligible || remaining == null || remaining <= 0
                            ? t("stepPicker.windowExpired")
                            : t("stepPicker.windowValid", { days: remaining })}
                        </Text>
                      </Group>
                    ) : null}
                  </Box>
                  <CaretRightIcon size={18} />
                </Group>
              </Paper>
            );
          })}

          {totalPages > 1 ? (
            <Group justify="flex-end" mt="xs">
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            </Group>
          ) : null}
        </Stack>
      )}
    </Stack>
  );
}
